/*======================================================================================================================
  * Bluebell: src/app/state/mappings/mappings.effects.ts
  * Last Edited: 11/03/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EffectHandlerService } from '@services/effect-handler.service';
import { ApiService } from '@state/api.service';
import { MappingActions } from '@state/mappings/mappings.actions';
import { concatMap, mergeMap, switchMap } from 'rxjs/operators';
import { Mapping, MappingCreatePartial, MappingPlot } from './mappings.interfaces';


@Injectable()
export class MappingsEffects {
  private actions$ = inject(Actions);
  private api = inject(ApiService);
  private handler = inject(EffectHandlerService);


  create$ = createEffect(() => this.actions$.pipe(
    ofType(MappingActions.create),
    concatMap((partial: MappingCreatePartial) => this.api.createMapping(partial).pipe(
      this.handler.effectResponse({
        retry: MappingActions.create(partial),
        success: (mapping: Mapping) => MappingActions.createSuccess(mapping),
        failure: MappingActions.createFailure(),
        failureMessage: 'Could not create mapping'
      })
    ))
  ));

  retrieve$ = createEffect(() => this.actions$.pipe(
    ofType(MappingActions.retrieve),
    switchMap(() => this.api.listMappings().pipe(
      this.handler.effectResponse({
        retry: MappingActions.retrieve(),
        success: (mappings: Mapping[]) => MappingActions.retrieveSuccess(mappings),
        failure: MappingActions.retrieveFailure(),
        failureMessage: 'Could not retrieve mappings'
      })
    ))
  ));

  rename$ = createEffect(() => this.actions$.pipe(
    ofType(MappingActions.rename),
    concatMap(({ name, uuid }) => this.api.renameMapping(uuid, name).pipe(
      this.handler.effectResponse({
        retry: MappingActions.rename({ name, uuid }),
        success: (mapping: Mapping) => MappingActions.renameSuccess(mapping),
        failure: MappingActions.renameFailure(),
        failureMessage: 'Could not rename mapping'
      })
    ))
  ));

  delete$ = createEffect(() => this.actions$.pipe(
    ofType(MappingActions.delete),
    mergeMap(({ uuid }) => this.api.deleteMapping(uuid).pipe(
      this.handler.effectResponse({
        retry: MappingActions.delete(uuid),
        success: () => MappingActions.deleteSuccess(uuid),
        failure: MappingActions.deleteFailure(),
        failureMessage: 'Could not delete mapping'
      })
    ))
  ));

  generateParetoData$ = createEffect(() => this.actions$.pipe(
    ofType(MappingActions.generateParetoData),
    mergeMap(({ uuid, mapData, performances}) => this.api.paretoGenerateData({ uuid, mapData, performances }).pipe(
      this.handler.effectResponse({
        retry: MappingActions.generateParetoData({ uuid, mapData, performances }),
        success: (pareto: MappingPlot[]) => MappingActions.generateParetoDataSuccess({ uuid, pareto }),
        failure: MappingActions.generateParetoDataFailure(),
        failureMessage: 'Could not generate pareto data'
      })
    ))
  ));

  generateCloudPlots$ = createEffect(() => this.actions$.pipe(
    ofType(MappingActions.generateCloudData),
    mergeMap(({ uuid, mapData, performances }) => this.api.cloudGenerateData({ uuid, mapData, performances }).pipe(
      this.handler.effectResponse({
        retry: MappingActions.generateCloudData({ uuid, mapData, performances }),
        success: (cloud: MappingPlot[]) => MappingActions.generateCloudDataSuccess({ uuid, cloud }),
        failure: MappingActions.generateCloudDataFailure(),
        failureMessage: 'Could not generate cloud data'
      })
    ))
  ));
}
